import { z } from 'zod'
import {
  detailingInterestKeys,
  packageKeys,
  protectionAddOnKeys,
  scheduleTimes,
  vehicleTypeOptions,
} from './booking-domain.ts'

const currentYear = new Date().getFullYear()
const phonePattern = /^[+\d][\d\s()+-]{7,24}$/
const datePattern = /^\d{4}-\d{2}-\d{2}$/

export const trimmedString = z.string().trim()

export const customerSchema = z.object({
  name: trimmedString.min(2, 'Enter the customer name.').max(120, 'Name is too long.'),
  email: trimmedString.email('Enter a valid email address.').max(180).transform((value) => value.toLowerCase()),
  phone: trimmedString.regex(phonePattern, 'Enter a valid phone number.').max(32),
  preferredContactMethod: z.enum(['Phone', 'Email', 'SMS']).default('Phone'),
})

export const vehicleSchema = z.object({
  type: z.enum(vehicleTypeOptions),
  make: trimmedString.min(1, 'Enter the vehicle make.').max(80),
  model: trimmedString.min(1, 'Enter the vehicle model.').max(80),
  year: trimmedString.regex(/^\d{4}$/, 'Enter a four-digit vehicle year.').refine((value) => {
    const year = Number(value)
    return year >= 1950 && year <= currentYear + 1
  }, 'Enter a sensible vehicle year.'),
  colour: trimmedString.min(1, 'Enter the vehicle colour.').max(60),
  registration: trimmedString.max(16, 'Registration is too long.').optional().default(''),
})

export const quotePreferredContactMethods = ['Phone', 'Email', 'Either'] as const

export const quoteCustomerSchema = customerSchema.extend({
  preferredContactMethod: z.enum(quotePreferredContactMethods).default('Either'),
})

export const quoteVehicleSchema = vehicleSchema.extend({
  colour: trimmedString.max(60).optional().default(''),
  year: z.union([
    trimmedString.regex(/^\d{4}$/, 'Enter a four-digit vehicle year.').refine((value) => {
      const year = Number(value)
      return year >= 1950 && year <= currentYear + 1
    }, 'Enter a sensible vehicle year.'),
    z.literal(''),
  ]).optional().default(''),
})

export const bookingCheckoutSchema = z.object({
  packageKey: z.enum(packageKeys),
  customer: customerSchema,
  vehicle: vehicleSchema,
  addOns: z.array(z.enum(protectionAddOnKeys)).max(8).default([]),
  preferredDate: trimmedString.regex(datePattern, 'Select a preferred date.'),
  preferredTime: z.enum(scheduleTimes),
  notes: trimmedString.max(2000, 'Notes are too long.').optional().default(''),
  termsAccepted: z.literal(true, { errorMap: () => ({ message: 'Accept the booking and deposit terms.' }) }),
  source: trimmedString.max(40).optional().default('website'),
})

export const depositCheckoutSchema = z.object({
  packageKey: z.enum(packageKeys),
  addOns: z.array(z.enum(protectionAddOnKeys)).max(8).default([]),
  noAdditionalProtection: z.boolean().default(false),
  publicReference: z
    .string()
    .regex(/^AGB-\d{4}-[A-F0-9]{6,12}$/i)
    .optional(),
})

export const quoteRequestSchema = z.object({
  customer: quoteCustomerSchema,
  vehicle: quoteVehicleSchema,
  serviceInterests: z.array(z.enum(detailingInterestKeys)).max(8).default([]).transform((keys) => (
    keys.length > 0 ? keys : ['unsure'] as typeof keys
  )),
  desiredOutcome: trimmedString.min(4, 'Enter the desired outcome.').max(2000),
  currentCondition: trimmedString.min(4, 'Enter the current condition.').max(2000),
  customerNotes: trimmedString.max(2000).optional().default(''),
  source: trimmedString.max(40).optional().default('website'),
  website: trimmedString.optional().default(''),
})

export const appointmentCustomerSchema = customerSchema.pick({
  name: true,
  email: true,
  phone: true,
})

export const appointmentVehicleSchema = z.object({
  make: trimmedString.min(1, 'Enter the vehicle make.').max(80),
  model: trimmedString.min(1, 'Enter the vehicle model.').max(80),
})

export const appointmentReservationSchema = z.object({
  publicReference: z
    .string()
    .regex(/^AGB-\d{4}-[A-F0-9]{6,12}$/i, 'A valid booking reference is required.')
    .optional(),
  packageKey: z.enum(packageKeys).optional(),
  addOns: z.array(z.enum(protectionAddOnKeys)).max(8).default([]),
  date: trimmedString.regex(datePattern, 'Choose an appointment date.'),
  time: trimmedString.regex(/^\d{2}:\d{2}$/, 'Choose an appointment time.'),
  customer: appointmentCustomerSchema,
  vehicle: appointmentVehicleSchema,
}).refine((value) => Boolean(value.publicReference || value.packageKey), {
  message: 'Select a ceramic protection level before choosing an appointment.',
  path: ['packageKey'],
})

export type BookingCheckoutInput = z.infer<typeof bookingCheckoutSchema>
export type QuoteRequestInput = z.infer<typeof quoteRequestSchema>
export type AppointmentReservationInput = z.infer<typeof appointmentReservationSchema>

export function zodFieldErrors(error: z.ZodError) {
  return error.issues.reduce<Record<string, string>>((result, issue) => {
    const key = issue.path.join('.')
    if (!result[key]) result[key] = issue.message
    return result
  }, {})
}
